import React from "react";
import styled from "styled-components";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Theme } from "../../Theme";
import { C4Text, T2Text } from "../text/CustomText";
import { Padding } from "../CommonBaseComponents";

const ChartWrapper = styled.div`
    width: 40%;
    height: 280px;
    padding: 20px;
    box-sizing: border-box;
`;

// 데이터 없을 때도 축/그리드는 그리기 위한 0 값 항목 (선·점은 투명 처리)
const EMPTY_AXIS_DATA = [
    { name: "1", value: 0 },
    { name: "2", value: 0 },
    { name: "3", value: 0 },
    { name: "4", value: 0 },
    { name: "5", value: 0 },
];
const EMPTY_Y_DOMAIN = [0, 2500];

function CustomTooltip({ active, payload, label, data, currency }) {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const rank = data.findIndex((d) => d.name === label) + 1;
        return (
            <div
                style={{
                    backgroundColor: Theme.color.white,
                    border: `1px solid ${Theme.color.zinc300}`,
                    borderRadius: "8px",
                    padding: "10px 16px",
                    boxShadow: `0 4px 12px ${Theme.color.black_01}`,
                    fontSize: 13,
                    fontWeight: 600,
                    color: Theme.color.gray800,
                    whiteSpace: "nowrap",
                    minWidth: 126,
                }}
            >
                <Padding padding={"3px 0"}>
                    <C4Text fontColor={Theme.color.zinc850}>Total {rank}</C4Text>
                </Padding>
                <T2Text fontColor={Theme.color.zinc850} fontWeight={"500"}>
                    {currency} {value.toLocaleString()}
                </T2Text>
            </div>
        );
    }

    return null;
}

/**
 * 순위별 값 추이 영역 차트.
 * @param data [{ name, value }] — name 은 순위 라벨, value 는 금액
 */
const PinkAreaChart = ({ data = [], currency = "฿" }) => {
    // 데이터 없으면 축/그리드(가로세로 눈금)는 그대로 두고 선·점만 렌더하지 않음
    const isEmpty = data.length === 0;
    const chartData = isEmpty ? EMPTY_AXIS_DATA : data;

    return (
        <ChartWrapper>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    {/* 그라데이션 정의 (영역 채우기용) */}
                    <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={Theme.color.rose400} stopOpacity={0.2} />
                            <stop offset="95%" stopColor={Theme.color.rose400} stopOpacity={0} />
                        </linearGradient>
                    </defs>

                    {/* 가로 그리드 선만 표시 */}
                    <CartesianGrid stroke={Theme.color.gray150} vertical={false} />

                    <XAxis
                        dataKey="name"
                        hide={true} // 이미지처럼 X축 라벨을 숨기려면 true
                    />

                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: Theme.color.gray400, fontSize: 14 }}
                        tickFormatter={(value) => value.toLocaleString()} // 천 단위 콤마
                        domain={isEmpty ? EMPTY_Y_DOMAIN : [0, "auto"]}
                    />

                    {/* 첫 번째 데이터 지점에 세로 점선 추가 */}
                    {!isEmpty && <ReferenceLine x="1" stroke={Theme.color.rose400} strokeDasharray="3 3" />}

                    {/* 메인 영역 그래프 — 축/그리드 계산이 그래프 요소에 의존하므로 빈 상태에서도 렌더하고 투명 처리 */}
                    <Area
                        type="monotone" // 부드러운 곡선
                        dataKey="value"
                        stroke={isEmpty ? "transparent" : Theme.color.rose400} // 선 색상
                        strokeWidth={2}
                        fillOpacity={isEmpty ? 0 : 1}
                        fill={isEmpty ? "transparent" : "url(#colorValue)"} // 위에서 정의한 그라데이션 사용
                        // 커스텀 점(Dot) 설정: 테두리가 있고 내부가 비어있는 형태
                        dot={
                            isEmpty
                                ? false
                                : {
                                      r: 6,
                                      fill: Theme.color.white,
                                      stroke: Theme.color.rose400,
                                      strokeWidth: 2,
                                  }
                        }
                        activeDot={isEmpty ? false : { r: 8 }}
                        isAnimationActive={!isEmpty}
                    />

                    {!isEmpty && (
                        <Tooltip content={<CustomTooltip data={data} currency={currency} />} isAnimationActive={false} />
                    )}
                </AreaChart>
            </ResponsiveContainer>
        </ChartWrapper>
    );
};

export default PinkAreaChart;
